/* @flow */

import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../libs/netgemLibrary/v8/types/RequestResponse';

import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { getLocationType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import { ItemLocationKind, type NETGEM_API_V8_ITEM_LOCATION } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { METADATA_KIND_PROGRAM, type MetadataKind, type NETGEM_API_V8_METADATA_PROGRAM } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import { logError } from '../../debug/debug';

type GetVodLocationsType = (locations: Array<NETGEM_API_V8_ITEM_LOCATION>, signal?: AbortSignal) => Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE>>;
type SendV8MetadataRequestType = (assetId: string, type: MetadataKind, signal?: AbortSignal) => Promise<NETGEM_API_V8_METADATA_PROGRAM>;
type LocationRequestType = (assetId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>;

const loadEpisodesVodLocationMetadata = async (
  locationsList: Array<Array<NETGEM_API_V8_ITEM_LOCATION>>,
  getVodLocations: GetVodLocationsType,
  signal?: AbortSignal,
): Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE> | null> => {
  const promises: Array<Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE>>> = [];

  for (let i = 0; i < locationsList.length; ++i) {
    const locations = locationsList[i];
    if (locations) {
      promises.push(getVodLocations(locations, signal));
    }
  }

  try {
    const results: Array<Array<NETGEM_API_V8_METADATA_SCHEDULE>> = await Promise.all(promises);
    if (signal?.aborted) {
      return null;
    }

    const vodLocationsMetadata: Array<NETGEM_API_V8_METADATA_SCHEDULE> = [];
    results.forEach((loc) => vodLocationsMetadata.push(...loc));
    return vodLocationsMetadata;
  } catch (error) {
    if (!signal) {
      logError(error.msg);
      return null;
    }
    await ignoreIfAborted(signal, error);
    return null;
  }
};

const loadEpisodesTVLocationMetadata = async (
  locationsList: Array<Array<NETGEM_API_V8_ITEM_LOCATION>>,
  getCatchupLocation: LocationRequestType,
  getRecordingLocation: LocationRequestType,
  signal?: AbortSignal,
): Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE> | null> => {
  const promises: Array<Promise<NETGEM_API_V8_REQUEST_RESPONSE>> = [];

  for (let i = 0; i < locationsList.length; ++i) {
    const locations = locationsList[i];
    if (locations && locations.length > 0) {
      const [location] = locations;
      const request = getLocationType(location.id) === ItemLocationKind.Catchup ? getCatchupLocation : getRecordingLocation;
      promises.push(request(location.id, signal));
    }
  }

  try {
    const results = await Promise.all(promises);
    if (signal?.aborted) {
      return null;
    }

    return results.map(({ result }) => result);
  } catch (error) {
    if (!signal) {
      logError(error.msg);
      return null;
    }
    await ignoreIfAborted(signal, error);
    return null;
  }
};

const loadEpisodeMetadataList = async (episodeIds: Array<string>, sendV8MetadataRequest: SendV8MetadataRequestType, signal?: AbortSignal): Promise<Array<NETGEM_API_V8_METADATA_PROGRAM> | null> => {
  const promises: Array<Promise<NETGEM_API_V8_METADATA_PROGRAM>> = episodeIds.map((id) => sendV8MetadataRequest(id, METADATA_KIND_PROGRAM, signal));

  try {
    const results = await Promise.all(promises);

    if (signal?.aborted) {
      return null;
    }

    return results;
  } catch (error) {
    if (!signal) {
      logError(error.msg);
    }

    return null;
  }
};

export { loadEpisodeMetadataList, loadEpisodesTVLocationMetadata, loadEpisodesVodLocationMetadata };
